import { z } from 'zod';

/**
 * The permission catalogue (doc 06 section 4.2).
 *
 * This is the single source of truth. The migration that seeds the
 * `permissions` table is generated from this list, and CI checks that every
 * registered route declares a permission that appears here -- a route with no
 * declared permission fails the build (doc 05 section 1, rule 5).
 */

export const SCOPE_LEVELS = ['platform', 'reseller', 'tenant', 'site'] as const;
export const ScopeLevelSchema = z.enum(SCOPE_LEVELS);
export type ScopeLevel = z.infer<typeof ScopeLevelSchema>;

export interface PermissionDef {
  key: string;
  resource: string;
  verb: string;
  description: string;
  min_scope: ScopeLevel;
}

const p = (
  key: string,
  description: string,
  min_scope: ScopeLevel = 'tenant',
): PermissionDef => {
  const [resource, ...rest] = key.split(':');
  return { key, resource: resource ?? key, verb: rest.join(':'), description, min_scope };
};

export const PERMISSIONS: readonly PermissionDef[] = [
  // tenancy
  p('tenant:read', 'View tenant details'),
  p('tenant:write', 'Modify tenant settings'),
  p('tenant:create', 'Create tenants', 'reseller'),
  p('tenant:delete', 'Delete tenants', 'reseller'),
  p('tenant:suspend', 'Suspend or resume a tenant', 'reseller'),
  p('reseller:read', 'View resellers', 'reseller'),
  p('reseller:write', 'Modify resellers', 'platform'),
  p('site:read', 'View sites'),
  p('site:write', 'Modify sites'),
  p('branding:write', 'Modify branding', 'reseller'),

  // identity
  p('user:read', 'View users'),
  p('user:invite', 'Invite users'),
  p('user:write', 'Modify users'),
  p('user:delete', 'Delete users'),
  p('role:read', 'View roles'),
  p('role:write', 'Modify roles'),
  p('membership:write', 'Assign roles to users'),
  p('apikey:read', 'View API keys'),
  p('apikey:write', 'Create or revoke API keys'),
  p('sso:write', 'Configure SSO'),
  // The keys for the services the platform talks to: Deepgram, Anthropic, OpenAI, the recordings
  // bucket, the email sender (0095). Their own permission rather than billing's, which is what
  // guarded them at first and read as nonsense on a transcription key (owner, 20 September 2026).
  p('providerkey:read', 'View which provider keys are set', 'platform'),
  p('providerkey:write', 'Add or replace a provider key', 'platform'),
  p('impersonate:start', 'Start an impersonation session', 'reseller'),

  // telephony
  p('extension:read', 'View extensions'),
  p('extension:write', 'Create or modify extensions'),
  p('extension:delete', 'Delete extensions'),
  p('extension:credentials:reveal', 'Reveal a SIP password'),
  // Self-service (docs/33 §4): the same, for the extensions linked to the user only.
  p('extension:read:own', 'View your own extensions'),
  p('extension:write:own', 'Change your own extension settings: forwarding, DND, voicemail PIN'),
  p('device:read', 'View devices'),
  p('device:write', 'Modify devices'),
  p('device:provision', 'Provision devices'),
  p('handsetmodel:write', 'Maintain the catalogue of supported handsets', 'platform'),
  // Client admins build their own account's templates too (owner, 15 September 2026).
  // Platform templates need it held at platform scope, which the database also enforces.
  p('provisioningtemplate:write', 'Build and change handset provisioning templates'),
  p('ringgroup:write', 'Modify ring groups'),
  p('queue:read', 'View queues'),
  p('queue:write', 'Modify queues'),
  p('queue:agent:manage', 'Manage queue agents'),
  p('voicemail:read', 'Read voicemail'),
  p('voicemail:read:own', 'Read your own voicemail'),
  p('voicemail:manage', 'Manage voicemail boxes'),
  p('featurecode:write', 'Modify feature codes'),
  // The client's own contacts (docs/32): never held above an account, see CLIENT_ONLY.
  p('phonebook:read', 'View the account phonebook and your own contacts'),
  p('phonebook:write', 'Change the account and department phonebooks'),
  // A seat's package sets its features AND its monthly charge, so choosing one
  // is the provider's decision, never the customer's.
  p('extension:package:write', 'Change the package or paid add-ons of an extension', 'reseller'),
  // A module (the dialler, Auto QA) is part of what an account is sold: the provider's switch (0130).
  p('module:write', 'Turn an account’s modules on and off', 'reseller'),
  // A new number is a new SIP username for every phone on the seat, and a report reads by number:
  // the provider's step, never a field the client edits (owner, 19 September 2026). History and
  // spend follow the extension (migration 0088).
  p('extension:renumber', 'Change an extension\'s number', 'reseller'),

  // numbering
  p('number:read', 'View numbers'),
  p('number:assign', 'Assign numbers'),
  p('number:release', 'Release numbers'),
  p('number:order', 'Order numbers', 'reseller'),
  p('port:read', 'View ports'),
  p('port:write', 'Manage ports'),
  p('inboundroute:write', 'Modify inbound routes'),

  // routing
  p('outboundroute:read', 'View outbound routes'),
  p('outboundroute:write', 'Modify outbound routes'),
  // Routing an account on OUR trunks is the provider's decision: which carrier a
  // call leaves by is a cost and a quality commitment we make. A customer on its
  // own trunks (BYOT) routes with outboundroute:write alone.
  p('outboundroute:manage', 'Change how an account on our trunks routes outbound calls', 'reseller'),
  p('dialpermission:write', 'Modify dial permissions'),

  // carriers
  p('carrier:read', 'View carriers', 'platform'),
  p('carrier:write', 'Modify carriers', 'platform'),
  p('trunk:read', 'View trunks', 'platform'),
  p('trunk:write', 'Modify trunks', 'platform'),
  // What a CUSTOMER may see of the trunks its calls use: the carrier's name and
  // whether it is up and registered -- never hosts or credentials, which
  // trunk:read shows. Deliberately NOT under trunk:, because the customer roles
  // below drop that whole prefix.
  p('connectivity:read', 'View the trunks this account uses and whether they are up'),
  // Bring your own trunk: the customer configures its OWN carrier account.
  // Only does anything once the provider has switched the account to BYOT.
  p('connectivity:write', 'Configure the account’s own trunk (bring your own trunk)'),

  // flows
  p('flow:read', 'View call flows'),
  p('flow:write', 'Modify call flows'),
  p('flow:publish', 'Publish call flows'),
  p('flow:template:manage', 'Manage flow templates', 'reseller'),

  // fleet
  p('node:read', 'View nodes', 'platform'),
  p('node:write', 'Modify nodes', 'platform'),
  p('node:command', 'Send node commands', 'platform'),
  p('node:drain', 'Drain a node', 'platform'),

  // cdr
  p('cdr:read', 'View call records'),
  p('cdr:read:own', 'View your own calls and their recordings'),
  p('cdr:export', 'Export call records'),
  p('recording:read', 'Play call recordings'),
  p('recording:delete', 'Delete call recordings'),

  // auto QA (docs/37). The words of a call are further guarded by the database: whoever holds
  // these, only the account's own people ever read a transcript (0119, arc.in_client_scope).
  p('qa:read', 'View QA runs, their scores and what they cost'),
  p('qa:read:own', 'View the QA of your own calls'),
  p('qa:write', 'Change QA rules and settings'),
  p('qa:run', 'Run QA on a call by hand'),
  p('qa:provider:write', 'Set the QA provider keys and accept their terms'),

  // dialler lists (docs/36 D1b, 0123). Client-only: leads are the client's (owner, 25 September 2026).
  p('dialler:read', 'View dialler lists and their leads'),
  p('dialler:write', 'Change dialler lists, import leads and keep the do-not-call list'),
  p('dialler:leadsource:write', 'Make and revoke the API clients that add leads to a list'),
  // Campaigns (docs/36 D1b, 0127): client-only like the leads they call.
  p('campaign:read', 'View campaigns and how far through their leads they are'),
  p('campaign:write', 'Create, change, start and pause campaigns'),
  p('dialler:agent', 'Work campaigns in the agent workspace'),
  // Toll fraud (docs/39): holds a rule placed on a seat or account, and the evidence.
  p('fraud:read', 'View fraud alerts and holds'),
  // Releasing a hold lets a seat that may be in a stranger's hands spend money again: the MSP's
  // call, not the client's (docs/16 Layer 4), and only after step-up.
  p('fraud:write', 'Place and release fraud holds, and review alerts', 'reseller'),

  // billing
  p('billing:read', 'View billing'),
  // Mode (prepaid or postpaid), rate deck, VAT and billing day are commercial
  // terms: a customer holding this could move itself onto a cheaper rate deck.
  p('billing:write', 'Modify billing terms', 'reseller'),
  p('ratedeck:read', 'View rate decks', 'reseller'),
  p('ratedeck:write', 'Modify rate decks', 'reseller'),
  p('invoice:read', 'View invoices'),
  p('invoice:issue', 'Issue invoices', 'reseller'),
  p('payment:record', 'Record payments', 'reseller'),
  // Buying airtime by card (docs/28 B1). Its own key rather than billing:read:
  // the read-only role holds every ':read', and a read-only user starting
  // payments would not be read-only.
  p('payment:make', 'Pay for airtime', 'tenant'),
  p('balance:adjust', 'Adjust balances', 'reseller'),
  p('creditlimit:write', 'Set credit limits', 'reseller'),

  // products
  p('product:write', 'Modify the product catalogue: packages, features and prices', 'platform'),

  // reporting
  p('report:read', 'View reports'),
  p('report:schedule', 'Schedule reports'),

  // audit
  p('audit:read', 'View the audit log'),

  // notifications
  p('webhook:read', 'View webhooks'),
  p('webhook:write', 'Modify webhooks'),
] as const;

export const PERMISSION_KEYS = PERMISSIONS.map((x) => x.key);
export type PermissionKey = (typeof PERMISSIONS)[number]['key'];

/**
 * Actions requiring MFA re-verification within 5 minutes (doc 06 section 4.4).
 * An impersonated session can never satisfy step-up.
 */
export const STEP_UP_PERMISSIONS: readonly string[] = [
  'extension:credentials:reveal',
  'apikey:write',
  'billing:write',
  // What a call costs is billing too: creating and changing billing must not be
  // callable from the public (owner, 15 September 2026).
  'ratedeck:write',
  // An invoice is a legal document, and a recorded payment settles money owed.
  'invoice:issue',
  'payment:record',
  'balance:adjust',
  'creditlimit:write',
  'sso:write',
  // A provider's API key is a credential like any other (0095).
  'providerkey:write',
  // So is an account's own QA key, and accepting a provider's terms sends calls offshore.
  'qa:provider:write',
  // A lead key lets anybody holding it add people to a list to be called.
  'dialler:leadsource:write',
  // Releasing a fraud hold re-opens a seat that may be compromised.
  'fraud:write',
  'impersonate:start',
  'node:drain',
];

/**
 * Permissions only an account's own users may hold, whatever a role says (owner, 17 September
 * 2026: "the MSP shouldnt even see the clients contacts"). A platform or reseller holder never
 * has them, even with every permission; the database refuses the same (migration 0072).
 */
export const CLIENT_ONLY_PERMISSIONS: ReadonlySet<string> = new Set([
  'phonebook:read', 'phonebook:write',
  // Leads are the client's alone too (owner, 25 September 2026; 0123).
  'dialler:read', 'dialler:write', 'dialler:leadsource:write',
  'campaign:read', 'campaign:write', 'dialler:agent',
]);

/** Built-in roles (doc 06 section 4.3). Seeded, not user-editable. */
export interface RoleDef {
  key: string;
  name: string;
  scope_level: ScopeLevel;
  description: string;
  permissions: readonly string[] | '*';
}

const READ_ONLY = PERMISSION_KEYS.filter((k) => k.endsWith(':read'));

export const BUILTIN_ROLES: readonly RoleDef[] = [
  {
    key: 'platform_admin',
    name: 'Platform Admin',
    scope_level: 'platform',
    description: 'Full control of the platform. MFA mandatory.',
    permissions: '*',
  },
  {
    key: 'platform_support',
    name: 'Platform Support',
    scope_level: 'platform',
    description: 'Read everything, impersonate, diagnose. No billing writes, no destructive actions.',
    permissions: [...READ_ONLY, 'impersonate:start', 'node:command'],
  },
  {
    key: 'platform_finance',
    name: 'Platform Finance',
    scope_level: 'platform',
    description: 'Billing, rate decks, invoices and payments. Read-only elsewhere.',
    permissions: [
      ...READ_ONLY, 'billing:write', 'ratedeck:write', 'invoice:issue',
      'payment:record', 'balance:adjust', 'creditlimit:write',
    ],
  },
  {
    key: 'reseller_admin',
    name: 'Reseller Admin',
    scope_level: 'reseller',
    description: 'Full control of own tenants, branding and retail rates. MFA mandatory.',
    permissions: PERMISSION_KEYS.filter(
      (k) => !k.startsWith('carrier:') && !k.startsWith('trunk:') && !k.startsWith('node:')
        // The platform's own keys are the platform's: a reseller sets its email sender on its own
        // branding tab and has no business with the recordings bucket or the AI providers.
        && !k.startsWith('providerkey:') && k !== 'reseller:write',
    ),
  },
  {
    key: 'reseller_support',
    name: 'Reseller Support',
    scope_level: 'reseller',
    description: 'Read plus support actions across own tenants.',
    permissions: [...READ_ONLY, 'impersonate:start'],
  },
  {
    key: 'tenant_admin',
    name: 'Tenant Admin',
    scope_level: 'tenant',
    description: 'Everything within the tenant except platform configuration.',
    permissions: PERMISSION_KEYS.filter(
      (k) =>
        !k.startsWith('carrier:') && !k.startsWith('trunk:') && !k.startsWith('node:') &&
        !k.startsWith('reseller:') && !k.startsWith('ratedeck:') && k !== 'tenant:create' &&
        k !== 'tenant:delete' && k !== 'tenant:suspend' && k !== 'impersonate:start',
    ),
  },
  {
    key: 'tenant_manager',
    name: 'Tenant Manager',
    scope_level: 'tenant',
    description: 'Extensions, devices, flows, queues and reports. No billing, no user management.',
    permissions: [
      'tenant:read', 'site:read', 'extension:read', 'extension:write', 'extension:delete',
      'device:read', 'device:write', 'device:provision', 'ringgroup:write',
      'queue:read', 'queue:write', 'queue:agent:manage', 'voicemail:read', 'voicemail:manage',
      'number:read', 'inboundroute:write', 'flow:read', 'flow:write', 'flow:publish',
      'cdr:read', 'cdr:export', 'recording:read', 'report:read',
      // "Are calls getting out?" is the first question when they are not.
      'connectivity:read',
      'phonebook:read', 'phonebook:write',
      'qa:read', 'qa:run', 'qa:write',
      'dialler:read', 'dialler:write',
      'campaign:read', 'campaign:write', 'dialler:agent',
    ],
  },
  {
    key: 'tenant_finance',
    name: 'Tenant Finance',
    scope_level: 'tenant',
    description: 'Invoices, balance and usage. No telephony configuration.',
    permissions: ['tenant:read', 'billing:read', 'invoice:read', 'report:read', 'cdr:read', 'payment:make'],
  },
  {
    key: 'tenant_user',
    // Named for the portal it signs in to (owner, 18 September 2026); the key stays tenant_user.
    name: 'TMS User',
    scope_level: 'tenant',
    description: 'Own extension settings, voicemail and call history.',
    permissions: [
      'tenant:read', 'extension:read:own', 'extension:write:own', 'voicemail:read:own', 'cdr:read:own',
      'phonebook:read',
      // Agents see their own QA by default: it is what makes QA coaching rather than
      // surveillance (docs/37 §6, decision 4 as recommended).
      'qa:read:own',
      // Work the campaigns they are put on (campaign_agents): nothing else of the dialler.
      'dialler:agent',
    ],
  },
  {
    key: 'receptionist',
    name: 'Receptionist',
    scope_level: 'tenant',
    description: 'Colleagues, queues, calls and the phonebook, to answer and pass calls on. No configuration.',
    permissions: [
      'tenant:read', 'site:read', 'extension:read', 'queue:read', 'cdr:read', 'voicemail:read',
      'phonebook:read', 'phonebook:write', 'connectivity:read',
    ],
  },
  {
    key: 'queue_supervisor',
    name: 'Queue Supervisor',
    scope_level: 'tenant',
    description: 'Queues and their agents, with the calls, recordings and reports behind them.',
    permissions: [
      'tenant:read', 'site:read', 'extension:read', 'queue:read', 'queue:write', 'queue:agent:manage',
      'cdr:read', 'cdr:export', 'recording:read', 'report:read',
      'qa:read', 'qa:run',
    ],
  },
  {
    key: 'reseller_billing',
    name: 'Reseller Billing',
    scope_level: 'reseller',
    description: 'Invoices, payments, balances and statements across own clients. No telephony.',
    permissions: [
      'tenant:read', 'reseller:read', 'billing:read', 'billing:write', 'invoice:read', 'invoice:issue',
      'payment:record', 'balance:adjust', 'creditlimit:write', 'report:read', 'cdr:read', 'cdr:export',
    ],
  },
  {
    key: 'reseller_technician',
    name: 'Reseller Technician',
    scope_level: 'reseller',
    description: 'Extensions, devices, provisioning and diagnostics across own clients. No billing or users.',
    permissions: [
      'tenant:read', 'site:read', 'reseller:read', 'extension:read', 'extension:write',
      'extension:credentials:reveal', 'device:read', 'device:write', 'device:provision',
      'provisioningtemplate:write', 'queue:read', 'number:read', 'connectivity:read', 'flow:read',
      'cdr:read', 'recording:read', 'impersonate:start',
    ],
  },
  {
    key: 'read_only',
    name: 'Read Only',
    scope_level: 'tenant',
    description: 'Read access only.',
    permissions: READ_ONLY,
  },
] as const;

/** Broadest first: a holder may hold permissions whose min_scope is its own level or narrower. */
const SCOPE_RANK: Record<ScopeLevel, number> = { platform: 0, reseller: 1, tenant: 2, site: 3 };

/**
 * Whether a holder at `level` may hold `key` at all.
 *
 * `min_scope` was recorded for every permission and enforced nowhere. The
 * tenant admin role is built as "everything except", so it quietly included
 * balance:adjust, creditlimit:write and billing:write: a customer's admin could
 * credit their own balance, raise their own credit limit or move onto a cheaper
 * rate deck. Applied when roles are seeded AND when a permission set is loaded
 * (apps/api/src/auth/permissions.ts and auth/apikeys.ts), so neither a role
 * definition nor a stale grant in the database can widen it.
 */
export function permissionAllowedAt(key: string, level: ScopeLevel): boolean {
  if (CLIENT_ONLY_PERMISSIONS.has(key) && (level === 'platform' || level === 'reseller')) return false;
  const def = PERMISSIONS.find((x) => x.key === key);
  return def !== undefined && SCOPE_RANK[def.min_scope] >= SCOPE_RANK[level];
}

/**
 * What somebody may put into a role, or hand to somebody (docs/33 §3.3): what they hold, and for
 * platform and reseller staff the client-only permissions as well.
 *
 * Staff never hold the client's phonebook (docs/32), so "nobody grants what they do not hold" left
 * them unable to invite a client's admin at all: every client role but two carries it (18 September
 * 2026). They may give it; they still cannot use it, since a staff member's own permission set
 * drops it (api auth/permissions.ts), and permissionAllowedAt keeps it out of any role above client
 * level.
 */
export function grantablePermissions(held: Iterable<string>, level: ScopeLevel): Set<string> {
  const out = new Set(held);
  if (level === 'platform' || level === 'reseller') for (const k of CLIENT_ONLY_PERMISSIONS) out.add(k);
  return out;
}

export function permissionsForRole(role: RoleDef): readonly string[] {
  const keys = role.permissions === '*' ? PERMISSION_KEYS : role.permissions;
  return keys.filter((k) => permissionAllowedAt(k, role.scope_level));
}
